import { ChangeDetectorRef, Component, EventEmitter, inject, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { LeafletDirective } from 'src/app/directives/leaflet/core/leaflet.directive';
import { AreaDTO, CoordinateClusterDTO } from 'src/app/services/api/api';
import { NominatimResult } from 'src/app/services/geo/model/nominatim';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { ThemingService } from '@ngx-seatback/utils';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  standalone: false,
})
export class MapComponent implements OnInit, OnDestroy {
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild(LeafletDirective) leaflet: LeafletDirective;
  iamready: boolean = false;
  @Input() opacity: number;
  @Output() readyEmitter = new EventEmitter<MapComponent>();
  @Output() mapZoomEnd = new EventEmitter();
  @Output() mapMoveEnd = new EventEmitter();
  @Output() initialPanReady = new EventEmitter();
  @Input() drawing: boolean;
  @Input() zoomControl: boolean = true;
  @Input() roundedCorners: boolean = false;
  dropdownTop?: number;
  dropdownLeft?: number;
  dropdownBottom?: number;
  dropdownRight?: number;
  dropdownDisplay: string = 'none';
  dropDownPxX?: number;
  dropDownPxY?: number;
  dropDownCoordX?: number;
  dropDownCoordY?: number;
  dropDownVisible: boolean = false;
  whatIsHerePopupVisible: boolean = false;
  whatIsHerePopup: L.Popup;
  themingSubscription: Subscription;
  lightLayer = L.tileLayer(this.util.geo.assets.getMap(null, 'light-theme'), {
    id: 'Light',
    maxZoom: 18,
    attribution: '',
  });
  darkLayer = L.tileLayer(this.util.geo.assets.getMap(null, 'dark-theme'), { id: 'Dark', maxZoom: 18, attribution: '' });
  @Output()
  singlePointSelected: EventEmitter<{ x: number; y: number }> = new EventEmitter<{ x: number; y: number }>();
  mHeight: string;
  mWidth: string;
  leafletOptions: any;
  leafletDrawOptions: any;
  map: L.Map;
  drawControl: L.Control.Draw;
  areasLayer: L.Layer[] = [];
  clusterLayer: L.Layer[] = [];
  singlepointLayer: L.Layer[] = [];
  that: MapComponent;
  initialPanEmitted: boolean = false;
  lastPanPosition: any;
  private clickListenerAttached: boolean = false;
  private rightClickListenerAttached: boolean = false;

  constructor(
    public readonly util: UtilService,
    public readonly errorHandler: ErrorService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly theming: ThemingService,
    public readonly translate: TranslateService,
    private readonly zone: NgZone
  ) {}

  _initialZoom: number;

  get initialZoom(): number {
    return this._initialZoom;
  }

  @Input() set initialZoom(val: number) {
    if (this._initialZoom !== val) {
      this._initialZoom = val;
      // this.applyInitialZoom();
    }
  }

  _showSinglePoint: any;

  get showSinglePoint(): any {
    return this._showSinglePoint;
  }

  @Input() set showSinglePoint(value: any) {
    if (value && value.x && value.y) {
      if (this._showSinglePoint?.x !== value.x || this._showSinglePoint?.y !== value.y) {
        this._showSinglePoint = value;
        this.refreshShowSinglePoint();
        this.refreshLayers();
        this.panSinglePoint();
        this.emitInitialPanReady();
      }
    }
  }

  _selectSinglePoint: boolean;

  get selectSinglePoint() {
    return this._selectSinglePoint;
  }

  @Input() set selectSinglePoint(value: boolean) {
    this._selectSinglePoint = value;
  }

  _whatIsHere: boolean;

  get whatIsHere() {
    return this._whatIsHere;
  }

  @Input() set whatIsHere(value: boolean) {
    this._whatIsHere = value;
  }

  _centerPoint: any;

  get centerPoint(): any {
    return this._centerPoint;
  }

  @Input() set centerPoint(value: any) {
    if (this._centerPoint !== value) {
      this._centerPoint = value;
      this.panCenterPoint();
    }
  }

  private _mapHeight: number;

  get mapHeight(): number {
    return this._mapHeight;
  }

  @Input() set mapHeight(value: number) {
    if (this._mapHeight !== value) {
      this._mapHeight = value;
      if (this._mapHeight) {
        this.mHeight = this._mapHeight + 'px';
      }

      this.applyResize();
    }
  }

  private _mapWidth: number;

  get mapWidth(): number {
    return this._mapWidth;
  }

  @Input() set mapWidth(value: number) {
    if (this._mapWidth !== value) {
      this._mapWidth = value;
      if (this._mapWidth) {
        this.mWidth = this._mapWidth + 'px';
      }

      this.applyResize();
    }
  }

  _coordinateClusters: CoordinateClusterDTO[];

  get coordinateClusters(): CoordinateClusterDTO[] {
    return this._coordinateClusters;
  }

  @Input() set coordinateClusters(value: CoordinateClusterDTO[]) {
    this._coordinateClusters = value;

    this.refreshCoordinateClusters();
    this.refreshLayers();
  }

  _areas: AreaDTO[];

  get areas(): AreaDTO[] {
    return this._areas;
  }

  @Input() set areas(value: AreaDTO[]) {
    this._areas = value;
    this.refreshAreas();
    this.refreshLayers();

    if (this._areas && this._areas.length > 0) {
      const group: L.FeatureGroup = new L.FeatureGroup(this.layers);
      this.fitBounds(group.getBounds(), { animate: false });
      this.emitInitialPanReady();
    } else {
      this.setView(L.latLng(51.3, 9.5), this.initialZoom ? this.initialZoom : 7, { animate: false });
    }
  }

  _layers: L.Layer[] = [];

  get layers(): L.Layer[] {
    return this._layers;
  }

  set layers(val: L.Layer[]) {
    this._layers = val;
  }

  fitBounds(bounds: L.LatLngBoundsExpression, options?: L.FitBoundsOptions) {
    if (this.map && this.leaflet && this.iamready) {
      this.leaflet.doResize();
      this.map.fitBounds(bounds, options);
    } else {
      setTimeout(() => {
        this.fitBounds(bounds, options);
      }, 250);
    }
  }

  setView(center: L.LatLngExpression, zoom: number, options?: L.ZoomPanOptions) {
    if (this.map && this.leaflet && this.iamready) {
      this.map.setView(center, zoom, options);
    } else {
      setTimeout(() => {
        this.setView(center, zoom, options);
      }, 250);
    }
  }

  emitInitialPanReady() {
    if (!this.initialPanEmitted) {
      // this.initialPanEmitted = true;
      setTimeout(() => {
        this.initialPanReady.emit();
      }, 500);
    }
  }

  refreshLayers() {
    let layers: L.Layer[] = [];
    layers = layers.concat(this.areasLayer).concat(this.clusterLayer).concat(this.singlepointLayer);
    this.layers = layers;

    // setTimeout(() => {
    //   for(let l of this.layers) {
    //     if(l.getTooltip()) {
    //       l.openTooltip();
    //     }
    //   }
    // }, 500);
  }

  refreshShowSinglePoint() {
    this.singlepointLayer = [];
    if (this.showSinglePoint) {
      const marker = L.marker([this.showSinglePoint.y, this.showSinglePoint.x], {
        icon: this.util.geo.assets.pin,
        draggable: this.selectSinglePoint,
      });
      if (this.showSinglePoint.popup) {
        marker.bindTooltip(this.showSinglePoint.popup, { direction: 'top', permanent: true });
      }
      if (this.selectSinglePoint) {
        marker.on('dragend', (e: L.LeafletEvent) => {
          const target = e.target;
          const position: L.LatLng = target.getLatLng();
          this.panTo(position.lat, position.lng, true);
          this.singlePointSelected.emit({ x: position.lng, y: position.lat });
        });
      }
      this.singlepointLayer.push(marker);
    }
  }

  attachClickListener() {
    if (this.map && this.leaflet && this.iamready) {
      if (!this.clickListenerAttached) {
        this.clickListenerAttached = true;
        this.map.on('click', e => {
          this.onClick(e);
        });
      }
    } else {
      setTimeout(() => {
        this.attachClickListener();
      }, 250);
    }
  }

  attachRightClickListener() {
    if (this.map && this.leaflet && this.iamready) {
      if (!this.rightClickListenerAttached) {
        this.rightClickListenerAttached = true;
        this.map.on('contextmenu', e => {
          this.onRightClick(e);
        });
      }
    } else {
      setTimeout(() => {
        this.attachRightClickListener();
      }, 250);
    }
  }

  onClick(e: L.LeafletEvent) {
    if (this.whatIsHere && this.whatIsHerePopupVisible) {
      this.hideWhatIsHerePopup();
      return;
    }
    if (this.whatIsHere && this.dropDownVisible) {
      this.hideDropdown();
      return;
    }
    if (this.selectSinglePoint) {
      this._showSinglePoint = { x: e['latlng']['lng'], y: e['latlng']['lat'] };
      this.singlePointSelected.emit(this.showSinglePoint);
      this.panSinglePoint(true);
      this.refreshShowSinglePoint();
    }
  }

  onRightClick(e: L.LeafletEvent) {
    if (this.whatIsHere) {
      const latlng: L.LatLng = L.latLng(e['latlng']['lat'], e['latlng']['lng']);
      this.dropDownPxX = e['containerPoint']['x'];
      this.dropDownPxY = e['containerPoint']['y'];
      this.dropDownCoordX = latlng.lng;
      this.dropDownCoordY = latlng.lat;

      this.showDropdown();
    }
  }

  showDropdown() {
    this.dropdownTop = null;
    this.dropdownLeft = null;
    this.dropdownBottom = null;
    this.dropdownRight = null;

    const mapWidth: number = this.map.getSize().x;
    const mapHeight: number = this.map.getSize().y;

    const useLeft: boolean = this.dropDownPxX <= mapWidth / 2;
    const useTop: boolean = this.dropDownPxY <= mapHeight / 2;

    if (useLeft) {
      this.dropdownLeft = this.dropDownPxX + 1;
    } else {
      this.dropdownRight = mapWidth - this.dropDownPxX - 1;
    }
    if (useTop) {
      this.dropdownTop = this.dropDownPxY + 1;
    } else {
      this.dropdownBottom = mapHeight - this.dropDownPxY - 1;
    }

    this.dropdownDisplay = 'block';

    this.dropDownVisible = true;

    this.changeDetector.detectChanges();
  }

  hideDropdown() {
    if (this.dropDownVisible) {
      this.dropdownTop = null;
      this.dropdownLeft = null;
      this.dropdownBottom = null;
      this.dropdownRight = null;
      this.dropdownDisplay = 'none';

      this.dropDownVisible = false;

      this.changeDetector.detectChanges();
    }
  }

  hideWhatIsHerePopup() {
    if (this.whatIsHerePopupVisible) {
      if (this.whatIsHerePopup) {
        this.whatIsHerePopup.remove();
      }
      this.whatIsHerePopup = null;
      this.whatIsHerePopupVisible = false;
    }
    this.changeDetector.detectChanges();
  }

  callDropdownAction(action) {
    action(this.that);
    this.hideDropdown();
  }

  options() {
    return [
      {
        text: this.translate.instant('lang.map.whatIsHere'),
        icon: 'search',
        enabled: true,
        action: (that: MapComponent) => {
          that.showWhatIsHere();
        },
      },
    ];
  }

  showWhatIsHere() {
    this.util.geo.geocoder
      .coordinatesToAddress({
        x: this.dropDownCoordX,
        y: this.dropDownCoordY,
      })
      .subscribe((e: NominatimResult[]) => {
        let html = '';
        html += this.util.geo.whatIsHereDiv(e, { y: this.dropDownCoordY, x: this.dropDownCoordX });

        this.whatIsHerePopup = L.popup({ closeButton: false })
          .setLatLng([this.dropDownCoordY, this.dropDownCoordX])
          .setContent(html)
          .openOn(this.map);
        this.whatIsHerePopupVisible = true;
        this.changeDetector.detectChanges();
      });
  }

  refreshCoordinateClusters() {
    this.clusterLayer = [];
    if (this.coordinateClusters && this.coordinateClusters.length > 0) {
      let min;
      let max;
      let offsetmax;
      const colors = {};
      for (const cluster of this.coordinateClusters) {
        if (!min) {
          min = cluster.size;
        }
        if (!max) {
          max = cluster.size;
        }
        if (min >= cluster.size) {
          min = cluster.size;
        }
        if (max <= cluster.size) {
          max = cluster.size;
        }
      }

      min = 0;

      offsetmax = max - min;

      for (const cluster of this.coordinateClusters) {
        colors['' + cluster.size + ''] = this.perc2color(((cluster.size - min) / offsetmax) * 100);
      }
      // for(let cluster of this.coordinateClusters) {
      //   let polys: L.LatLng[] = [];
      //   for(let fuck of cluster.hull) {
      //     let latlng: L.LatLng = L.latLng(fuck.coordinates[1], fuck.coordinates[0]);
      //     polys.push(latlng);
      //   }
      //   let poly = L.polygon(polys);//, {color: colors["" + cluster.size + ""]});
      //   //circle = L.circle([cluster.center.y, cluster.center.x], {radius: cluster.radius, fillOpacity: ((cluster.size - min) / offsetmax)});
      //   this.clusterLayer.push(poly);
      // }
      // for(let cluster of this.coordinateClusters) {
      //   let circle = L.circle([cluster.center.coordinates[1], cluster.center.coordinates[0]],
      //      {radius: this.util.geo.circleRadiusByZoom(this.map.getZoom(), 16, this.map.getCenter()), color: colors["" + cluster.size + ""], fillOpacity: 0.6});
      //   //circle = L.circle([cluster.center.y, cluster.center.x], {radius: cluster.radius, fillOpacity: ((cluster.size - min) / offsetmax)});
      //   this.clusterLayer.push(circle);
      // }
      for (const cluster of this.coordinateClusters) {
        const marker = L.marker([cluster.center.coordinates[1], cluster.center.coordinates[0]], {
          icon: this.util.geo.assets.getCircleIconBySize(cluster.size),
        });
        this.clusterLayer.push(marker);

        const displayedNumber: number = cluster.hasOwnProperty('screens') ? (cluster as any).screens : cluster.size;
        this.clusterLayer.push(
          L.marker([cluster.center.coordinates[1], cluster.center.coordinates[0]], {
            icon: L.divIcon({
              html:
                '<div class="' +
                this.util.geo.assets.getCircleClassBySize(cluster.size) +
                ' ' +
                'leaflet-bubble-text-container' +
                '"><span class="leaflet-bubble-text">' +
                this.stringTransformation.transformNumber(displayedNumber) +
                '</span></div>',
              className: this.util.geo.assets.getCircleClassBySize(cluster.size),
            }),
          })
        );
      }
    }
  }

  refreshAreas() {
    this.areasLayer = [];
    if (this.areas && this.areas.length > 0) {
      for (const area of this.areas) {
        for (const polygon of area.polygons) {
          this.areasLayer.push(L.geoJSON(polygon.data ? polygon.data : polygon.simple ? polygon.simple : polygon.mbr));
        }
      }
    }
  }

  perc2color(perc: number) {
    const turnaround = 100 - perc;
    let r;
    let g;
    const b = 0;
    if (turnaround < 50) {
      r = 255;
      g = Math.round(5.1 * turnaround);
    } else {
      g = 255;
      r = Math.round(510 - 5.1 * turnaround);
    }
    const h = r * 0x10000 + g * 0x100 + b * 0x1;
    return '#' + ('000000' + h.toString(16)).slice(-6);
  }

  panSinglePoint(animate: boolean = false) {
    if (this.showSinglePoint) {
      if (this.showSinglePoint.zoom) {
        this.map.setZoom(this.showSinglePoint.zoom);
      }
      this.panTo(this.showSinglePoint.y, this.showSinglePoint.x, animate);
    }
  }

  panCenterPoint() {
    if (this.centerPoint) {
      this.panTo(this.centerPoint.y, this.centerPoint.x);
    }
  }

  panTo(pY, pX, pAnimate = false) {
    if (this.map && this.iamready) {
      let dopan: boolean = true;
      if (this.lastPanPosition) {
        if (this.lastPanPosition.x === pX && this.lastPanPosition.y === pY) {
          dopan = false;
        }
      }
      if (dopan) {
        this.leaflet.doResize();
        this.lastPanPosition = { y: pY, x: pX };
        this.map.setView(new L.LatLng(pY, pX), this.map.getZoom(), { animate: pAnimate });
        setTimeout(() => {
          this.emitInitialPanReady();
        }, 250);
      }
    } else {
      setTimeout(() => {
        this.panTo(pY, pX);
      }, 250);
    }
  }

  onMapReady(map: L.Map) {
    this.map = map;
    this.applyResize();
  }

  onDrawReady(drawControl: L.Control.Draw) {
    this.drawControl = drawControl;

    this.applyResize();
  }

  applyResize() {
    if (this.map && this.leaflet && this.iamready) {
      this.leaflet.delayResize();
    } else {
      setTimeout(() => {
        this.applyResize();
      }, 1000);
    }
  }

  ngOnInit() {
    this.leafletOptions = {
      layers: this.theming.currentTheme === 'dark-theme' ? [this.darkLayer] : [this.lightLayer],
      attributionControl: false,
      zoom: this.initialZoom ? this.initialZoom : 7,
      zoomControl: this.zoomControl,
      center: this.showSinglePoint ? L.latLng(this.showSinglePoint.y, this.showSinglePoint.x) : L.latLng(51.3, 9.5), // Kassel (Kalender Standard fuer Deutschland)
    };

    this.leafletDrawOptions = {
      position: 'topleft',
      draw: {
        rectangle: { showArea: false },
        marker: this.util.geo.assets.pin,
      },
    };

    this.themingSubscription = this.theming.theme.subscribe((theme: string) => {
      this.switchLayer(theme);
    });

    this.that = this;

    this.iamready = true;

    this.readyEmitter.emit(this);

    this.attachClickListener();
    this.attachRightClickListener();
  }

  switchLayer(selectedTheme: string) {
    if (this.map) {
      if (selectedTheme === 'dark-theme') {
        if (this.map.hasLayer(this.lightLayer)) {
          this.map.removeLayer(this.lightLayer);
        }
        this.map.addLayer(this.darkLayer);
      } else {
        if (this.map.hasLayer(this.darkLayer)) {
          this.map.removeLayer(this.darkLayer);
        }
        this.map.addLayer(this.lightLayer);
      }
    }
  }

  ngOnDestroy() {
    this.themingSubscription.unsubscribe();
  }

  emitMapZoomEnd() {
    this.mapZoomEnd.emit();
  }

  emitMapMoveEnd() {
    this.mapMoveEnd.emit();
  }

  getBoundingRectangle(): L.LatLngBounds {
    if (this.map) {
      return this.map.getBounds();
    }
    return null;
  }
}
